import React, { useMemo, useCallback } from "react";
import { Select, SelectProps, Group, Text, Box, Badge } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import {
  CAR_STATUS_OPTIONS,
  getCarStatusIcon,
  getCarStatusColor,
} from "@/config/carStatuses";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface CarStatusSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected car status value */
  value?: string | number | null | undefined;

  /** Callback fired when car status changes: (name, value) */
  onChange?: (name: string, value: string | null) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Option Data Interface
// ============================================

interface CarStatusOptionData {
  value: string;
  label: string;
  labelShort?: string;
  iconName: string;
  color: string;
}

// ============================================
// Component
// ============================================

const CarStatusSelectorComponent: React.FC<CarStatusSelectorProps> = ({
  name = "carStatus",
  value,
  onChange,
  placeholder,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
  ...selectProps
}) => {
  const { t } = useTranslation(["cars", "common"]);

  // Default translations
  const defaultPlaceholder =
    placeholder || t("common:selectors.carStatus.placeholder");
  const defaultLabel = label ?? t("common:selectors.carStatus.label");

  // Normalize value to string
  const normalizedValue = useMemo(() => {
    if (value == null) return null;
    return String(value);
  }, [value]);

  // Build options list with translated labels
  const carStatusOptions = useMemo((): CarStatusOptionData[] => {
    return CAR_STATUS_OPTIONS.map((option) => ({
      value: option.value,
      label: t(`common:${option.labelKey}`),
      //labelShort: t(`common:${option.labelShortKey}`),
      iconName: option.iconName,
      color: option.color,
    }));
  }, [t]);

  // Get current icon and color for left section
  const currentIcon = useMemo(() => getCarStatusIcon(normalizedValue), [normalizedValue]);
  const currentColor = useMemo(() => getCarStatusColor(normalizedValue), [normalizedValue]);

  // Handle onChange
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      onChange?.(name, selectedValue);
    },
    [name, onChange]
  );

  // Render option for dropdown
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as CarStatusOptionData;
      return (
        <Group gap="sm" wrap="nowrap">
          <AppIcon iconName={optionData.iconName} size={20} color={optionData.color} />
          <Text size="md" fw={500}>
            {optionData.label}
          </Text>
          {!!optionData.labelShort && <Badge size="sm" variant="light" color={optionData.color}>
            {optionData.labelShort}
          </Badge>}
        </Group>
      );
    },
    []
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    return <AppIcon iconName={currentIcon} size={18} color={currentColor} />;
  }, [currentIcon, currentColor]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={normalizedValue}
        onChange={handleChange}
        placeholder={defaultPlaceholder}
        clearable={!required}
        required={required}
        allowDeselect={!required}
        disabled={disabled}
        error={error}
        data={carStatusOptions}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
      />
    </Box>
  );
};

export const CarStatusSelector = React.memo(CarStatusSelectorComponent);